/**
 * stripeAppearance - themes the Stripe Payment Element + Express Checkout
 * Element to Telify's dark/terracotta look via the Appearance API
 * (P5-D17-F9, T-F9-3).
 *
 * CWR 2.6: re-pinned to the warm-luxe payment-page mockup `:root`
 * (docs/mockups/client-dashboard-redesign/payment-page.html) — the reason F9
 * chose Elements over Embedded Checkout (full code-level theming so the card
 * screen matches Telify, not a foreign Stripe surface). The iframe is cross-origin
 * and can't read the injected per-tenant theme tokens, so these are the resolved
 * warm-amber preset values (the mockup's reference palette). Snapshot-tested so a
 * token drift is caught.
 */

import type { Appearance, CssFontSource, StripePaymentElementOptions } from '@stripe/stripe-js';

/**
 * Appearance mapping (Telify warm-luxe). `theme: 'night'` is the dark base; the
 * variables below override it with the payment-mockup tokens.
 *
 * CWR 2.6 polish: the `rules` block re-skins the Payment Element's accordion rows
 * (Card / Bank-via-Link) to read as ONE family with the Venmo `SourceRow`, and the
 * fields INSIDE each row (`.Input`/`.Label`/…) to match the /details booking-form
 * inputs (`fields.module.css` `[data-form-appearance='clientBooking']`) instead of
 * Stripe's brown night-theme defaults. Values are the resolved warm-amber reference
 * literals (same rule as the variables above: the iframe is cross-origin and can't
 * see the injected per-tenant tokens), mirroring `SourceRow.module.css` +
 * `fields.module.css`.
 */
export const appearance: Appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#F26A14',
    colorBackground: '#1A130D',
    colorText: '#FAF3E8',
    colorTextSecondary: 'rgba(250, 243, 232, 0.62)',
    // Placeholder = the booking form's `--text-muted` (fields.module.css .input::placeholder).
    colorTextPlaceholder: 'rgba(250, 243, 232, 0.24)',
    colorDanger: '#F87171',
    borderRadius: '14px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    // Accordion rows — match the SourceRow label (--text-primary, 600) + 11px gap.
    accordionItemSpacing: '11px',
    accordionItemLabelColorText: '#FAF3E8',
    accordionItemLabelFontWeight: '600',
    accordionItemLabelSelectedColorText: '#FAF3E8',
    accordionItemLabelSelectedFontWeight: '600',
  },
  rules: {
    // .src (SourceRow): --bg-inset fill, --border-soft hairline, 14px radius, no shadow.
    '.AccordionItem': {
      backgroundColor: 'rgba(8, 5, 3, 0.55)',
      border: '1px solid rgba(250, 243, 232, 0.10)',
      borderRadius: '14px',
      padding: '11px 13px',
      boxShadow: 'none',
    },
    '.AccordionItem:hover': {
      backgroundColor: 'rgba(8, 5, 3, 0.55)',
    },
    // .src.sel: the accent selection border (rgba(--accent-rgb, 0.55)).
    '.AccordionItem--selected': {
      backgroundColor: 'rgba(8, 5, 3, 0.55)',
      borderColor: 'rgba(242, 106, 20, 0.55)',
    },
    // Field internals → the booking-form inputs (fields.module.css clientBooking):
    // --bg-inset fill, --text-muted hairline, 14px radius, accent focus + soft glow.
    '.Input': {
      backgroundColor: 'rgba(8, 5, 3, 0.55)',
      border: '1px solid rgba(250, 243, 232, 0.24)',
      borderRadius: '14px',
      color: '#FAF3E8',
      boxShadow: 'none',
    },
    '.Input:focus': {
      backgroundColor: 'rgba(8, 5, 3, 0.55)',
      borderColor: 'rgba(242, 106, 20, 0.55)',
      boxShadow: '0 0 18px rgba(242, 106, 20, 0.14)',
    },
    '.Input--invalid': {
      borderColor: '#F87171',
      boxShadow: '0 0 0 3px rgba(248, 113, 113, 0.15)',
    },
    // Field labels ("Card number", …) — the form's muted --text-tertiary label.
    '.Label': {
      color: 'rgba(250, 243, 232, 0.38)',
      fontSize: '13.5px',
      fontWeight: '500',
    },
    '.Error': {
      color: '#F87171',
      fontSize: '12px',
    },
    // Grouped sub-blocks + the Payment Element's "Save my info" consent — same inset
    // recipe so nothing inside the Card/Bank panels reads off-theme.
    '.Block': {
      backgroundColor: 'rgba(8, 5, 3, 0.55)',
      border: '1px solid rgba(250, 243, 232, 0.24)',
      borderRadius: '14px',
      boxShadow: 'none',
    },
    '.CheckboxLabel': {
      color: 'rgba(250, 243, 232, 0.62)',
      fontSize: '12.5px',
    },
  },
};

const COLOR = /^(#[0-9a-f]{3,8}|rgba?\([^)]*\))$/i;
const TRIPLET = /^\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}$/;

/**
 * client-pages-admin-palette — the same mapping, read from the studio's look.
 * The iframe can't resolve `var(--…)`, so this reads the injected client tokens
 * off <html> and hands Stripe concrete colors; the base theme follows the page
 * ground (light look → Stripe's light base). With no look on the page (no
 * `--bg-page`), the reference `appearance` above is returned unchanged.
 */
export function lookAppearance(): Appearance {
  const style = getComputedStyle(document.documentElement);
  const read = (name: string) => style.getPropertyValue(name).trim();
  if (!COLOR.test(read('--bg-page'))) return appearance;
  const color = (name: string, fallback: string) => (COLOR.test(read(name)) ? read(name) : fallback);
  const rgb = (name: string, fallback: string) => (TRIPLET.test(read(name)) ? read(name) : fallback);

  const fg = rgb('--fg-page-rgb', '250, 243, 232');
  const accent = rgb('--accent-rgb', '242, 106, 20');
  const dangerRgb = rgb('--danger-rgb', '248, 113, 113');
  const [r, g, b] = rgb('--bg-page-rgb', '5, 4, 3').split(',').map(Number);
  const lightGround = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;

  const text = color('--text-primary', '#FAF3E8');
  const inset = color('--bg-inset', 'rgba(8, 5, 3, 0.55)');
  const danger = color('--danger', '#F87171');
  const selectedBorder = `rgba(${accent}, 0.55)`;

  return {
    ...appearance,
    theme: lightGround ? 'stripe' : 'night',
    variables: {
      ...appearance.variables,
      colorPrimary: color('--accent', '#F26A14'),
      colorBackground: color('--bg-card', '#1A130D'),
      colorText: text,
      colorTextSecondary: color('--text-secondary', `rgba(${fg}, 0.62)`),
      colorTextPlaceholder: `rgba(${fg}, 0.24)`,
      colorDanger: danger,
      accordionItemLabelColorText: text,
      accordionItemLabelSelectedColorText: text,
    },
    rules: {
      ...appearance.rules,
      '.AccordionItem': { ...appearance.rules?.['.AccordionItem'], backgroundColor: inset, border: `1px solid rgba(${fg}, 0.10)` },
      '.AccordionItem:hover': { backgroundColor: inset },
      '.AccordionItem--selected': { backgroundColor: inset, borderColor: selectedBorder },
      '.Input': { ...appearance.rules?.['.Input'], backgroundColor: inset, border: `1px solid rgba(${fg}, 0.24)`, color: text },
      '.Input:focus': { backgroundColor: inset, borderColor: selectedBorder, boxShadow: `0 0 18px rgba(${accent}, 0.14)` },
      '.Input--invalid': { borderColor: danger, boxShadow: `0 0 0 3px rgba(${dangerRgb}, 0.15)` },
      '.Label': { ...appearance.rules?.['.Label'], color: `rgba(${fg}, 0.38)` },
      '.Error': { ...appearance.rules?.['.Error'], color: danger },
      '.Block': { ...appearance.rules?.['.Block'], backgroundColor: inset, border: `1px solid rgba(${fg}, 0.24)` },
      '.CheckboxLabel': { ...appearance.rules?.['.CheckboxLabel'], color: `rgba(${fg}, 0.62)` },
    },
  };
}

/**
 * Payment Element layout — spaced accordion so each method (Card / Bank) is its own
 * rounded card that the `.AccordionItem` rules above can style to match the Venmo
 * SourceRow. Without `spacedAccordionItems`, the default connected list shares edges
 * and the per-row radius/border can't read as separate source rows.
 */
export const paymentElementLayout: StripePaymentElementOptions['layout'] = {
  type: 'accordion',
  spacedAccordionItems: true,
};

/**
 * No custom Element font (CWR 0.4 — decision C). The Stripe Element iframe is
 * cross-origin and can't see the host @fontsource bundle; rather than load a CDN
 * face (which would reintroduce a Google-Fonts CDN dependency into a served
 * surface), the card input uses a system stack. The surrounding UI renders Outfit.
 */
export const elementsFonts: CssFontSource[] = [];
